// Guard for the names of uploaded files.  See backend/src/models.py for
// details.
export const FILE_NAME_LIMIT = 512;

export const FormMapper = {
  // All the "themes" are automagically generated from backend's
  // util.py::THEME_VISION_DICT.
  theme1: "Discrimination in Life Opportunities",
  "theme1-1": "Defining and evaluating fairness and other relevant values",
  "theme1-2": "Identifying, attributing, and detecting bias",
  "theme1-3": "Societal, cultural, organisational values and attunement",
  theme2: "Copyright Issues",
  "theme2-1": "Data training and input",
  "theme2-2": "Output infringement and AI-generated content",
  "theme2-3": "Economic, behavioural and governance considerations",
  theme3: "Social Resilience",
  "theme3-1": "Cultural awareness of AI and interaction",
  "theme3-2": "Dependence of AI on skills or relationships",
  "theme3-3": "AI regulation",
  theme4: "Control of Agents",
  "theme4-1": "Alignment and safety",
  "theme4-2": "Communication",
  "theme4-3": "Evaluation and trust",
  core1: "Cognitive modelling and systems",
  core2: "Game theory and economic paradigms",
  core3: "Heuristic search and optimization",
  core4: "Knowledge representation and reasoning",
  core5: "Machine Learning",
  core6: "Multiagent Systems",
  core7: "Natural Language Processing (NLP)",
  core8: "Planning and Scheduling",
  core9: "Reasoning under uncertainty",
  core10: "Robotics",
  core11: "Search and constraint satisfaction",
  core12: "Vision",
  nus: "National University of Singapore (NUS)",
  ntu: "Nanyang Technological University (NTU)",
  smu: "Singapore Management University (SMU)",
  suss: "Singapore University of Social Science (SUSS)",
  sutd: "Singapore University of Technology and Design (SUTD)",
  sit: "Singapore Institute of Technology (SIT)",
  "a-star": "Agency for Science, Technology and Research (A*STAR)",
  "create-cares":
    "Campus for Research Excellence And Technological Enterprise (CREATE) - CARES",
  "create-sec":
    "Campus for Research Excellence And Technological Enterprise (CREATE) - Singapore-ETH",
  "create-imperial":
    "Campus for Research Excellence And Technological Enterprise (CREATE) - Imperial Global Singapore",
  "create-smart":
    "Campus for Research Excellence And Technological Enterprise (CREATE) - Singapore-MIT Alliance for Research and Technology (SMART)",
  "PI[AI]": "Principal Investigator (Technical AI Expert)",
  "PI[SH]": "Principal Investigator (SHAPE/HASS Expert)",
  "Co-PI[AI]": "Co-Principal Investigator (Technical AI Expert)",
  "Co-PI[SH]": "Co-Principal Investigator (SHAPE/HASS Expert)",
  collab: "Collaborator",
  "in-kind": "In Kind",
  cash: "Cash",
};

export const SysMapper = {
  draft: "Draft",
  "complete-un": "Completed (Unendorsed)",
  reject: "Rejected",
  conditional_award: "Conditional Offer",
  "complete-en": "Completed (Endorsed)",
  "revise-cluster": "Revision Request (Host Institution)",
  "revise-aisg-r": "Revision Request",
  "revise-aisg-c": "Revision Completed",
  proceed: "Proceed to Stage 2",
  awarded: "Awarded",
  nus: "National University of Singapore (NUS)",
  ntu: "Nanyang Technological University (NTU)",
  smu: "Singapore Management University (SMU)",
  suss: "Singapore University of Social Science (SUSS)",
  sutd: "Singapore University of Technology and Design (SUTD)",
  sit: "Singapore Institute of Technology (SIT)",
  "a-star": "Agency for Science, Technology and Research (A*STAR)",
  "create-cares":
    "Campus for Research Excellence And Technological Enterprise (CREATE) - CARES",
  "create-sec":
    "Campus for Research Excellence And Technological Enterprise (CREATE) - Singapore-ETH",
  "create-imperial":
    "Campus for Research Excellence And Technological Enterprise (CREATE) - Imperial Global Singapore",
  "create-smart":
    "Campus for Research Excellence And Technological Enterprise (CREATE) - Singapore-MIT Alliance for Research and Technology (SMART)",
  aisg: "AI Singapore",
  "reviewer-local": "Reviewer (Local)",
  "reviewer-inter": "Reviewer (International)",
  "admin-super": "Administrator (Superuser)",
  "admin-cluster": "Administrator (Cluster)",
  submitter: "User (Submission)",
  technology: "AI Technology",
  innovation: "AI Innovation",
  research: "AI Research",
};

// Helper method to avoid retyping the text that is already done/used in
// RevMapper while building out the form fields later on in app_review_admin.
const _pull_as_options = (inner) => {
  let out = [];

  for (const entry of Object.entries(inner)) {
    out.push({
      value: entry[0].toString(),
      label: entry[1],
    });
  }
  return out.reverse();
};

// Refactored out value mapping.
const _EVAL_SCALE = {
  4: "4 - Excellent",
  3: "3 - Good",
  2: "2 - Average",
  1: "1 - Poor",
  0: "0 - Unable to Judge / Not Applicable",
};
const _RESOURCE_SCALE = {
  4: "4 - Adequate",
  3: "3 - Excessive",
  2: "2 - Inadequate",
  1: "1 - Unable to Judge",
};

export const RevMapper = {
  support_outcome: {
    support: "Supported",
    "not-support": "Not Supported",
  },
  scientific: _EVAL_SCALE,
  novelty: _EVAL_SCALE,
  feasibility: _EVAL_SCALE,
  quality_integration: _EVAL_SCALE,
  suitability: _EVAL_SCALE,
  impact: _EVAL_SCALE,
  manpower: _RESOURCE_SCALE,
  equipment: _RESOURCE_SCALE,
  other_operating: _RESOURCE_SCALE,
  overall_score: {
    4: "4 - Excellent: Proposal is of outstanding quality, I would like to highly recommend it to being funded.",
    3: "3 - Good: Proposal is of good quality, I think it could be funded.",
    2: "2 - Average: Proposal is lacking in some areas, but it has merits that outweigh its flaws. It can be considered after certain issues are addressed.",
    1: "1 - Poor: Proposal is lacking in one or more critical aspect, key issues are not sufficiently addressed. Its flaws outweigh its merits. Hence, it is insufficiently competitive and below the acceptance threshold.",
    0: "0 - Unable to Judge.",
  },
  level_of_confidence: {
    4: "4 - Expert in the research area.",
    3: "3 - Familiar with the topic/research area.",
    2: "2 - Somewhat familiar with the topic/research area.",
    1: "1 - Not familiar with the topic/research area – can review for general AI topic of interest.",
  },
};

/* Proposal */
export const proposal = {
  title: {
    elementType: "input-inline",
    elementConfig: {
      type: "text",
      placeholder: "enter a title for the proposal (max 200 characters)",
      isValid: false,
      isInvalid: false,
      required: true,
      disabled: false,
    },
    elementDecorators: {
      label: "[SECTION 1.1] Proposal Title",
      sublabel: "",
      feedback: "valid",
      feedbackMsg: "",
    },
    validation: {
      isText: true,
      maxLength: 200,
      required: true,
    },
    value: "<Title>",
    valid: false,
    touched: false,
  },

  period_of_support: {
    elementType: "input-inline",
    elementConfig: {
      type: "number",
      placeholder: "enter number of years (numerical only)",
      isValid: false,
      isInvalid: false,
      required: true,
      disabled: false,
    },
    elementDecorators: {
      label: "[SECTION 1.2] Period of Support",
      sublabel: "(Number of years)",
      feedback: "valid",
      feedbackMsg: "",
    },
    validation: {
      isDecimal: false,
      maxLength: 10,
      required: true,
    },
    value: "",
    valid: false,
    touched: false,
  },

  host_institution: {
    elementType: "select-inline",
    elementConfig: {
      options: [
        { value: "", label: "<----- Please select from drop-down menu ----->" },
        {
          value: "a-star",
          label: "Agency for Science, Technology and Research (A*STAR)",
        },
        { value: "nus", label: "National University of Singapore (NUS)" },
        { value: "ntu", label: "Nanyang Technological University (NTU)" },
        { value: "smu", label: "Singapore Management University (SMU)" },
        {
          value: "sutd",
          label: "Singapore University of Technology and Design (SUTD)",
        },
        { value: "sit", label: "Singapore Institute of Technology (SIT)" },
        {
          value: "suss",
          label: "Singapore University of Social Sciences (SUSS)",
        },
        {
          value: "create-cares",
          label:
            "Campus for Research Excellence And Technological Enterprise (CREATE) - CARES",
        },
        {
          value: "create-sec",
          label:
            "Campus for Research Excellence And Technological Enterprise (CREATE) - Singapore-ETH",
        },
        {
          value: "create-imperial",
          label:
            "Campus for Research Excellence And Technological Enterprise (CREATE) - Imperial Global Singapore",
        },

        {
          value: "create-smart",
          label:
            "Campus for Research Excellence And Technological Enterprise (CREATE) - Singapore-MIT Alliance for Research and Technology (SMART)",
        },
      ],
      isValid: false,
      isInvalid: false,
      required: true,
    },
    elementDecorators: {
      label: "[SECTION 1.3] Host Institution",
      sublabel: "",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
  },

  theme: {
    elementType: "select-inline",
    elementConfig: {
      options: [
        {
          value: "",
          label: "<------ Please select a vision from drop-down ------>",
        },
      ].concat(
        ((_) => {
          // This lambda automatically extracts the big-level themes.
          let out = [];
          for (const entry of Object.entries(_)) {
            if (entry[0].startsWith("theme") && !entry[0].includes("-")) {
              out.push({
                value: entry[0],
                label: entry[1],
              });
            }
          }
          return out.sort((a, b) => {
            return a.value.localeCompare(b.value);
          });
        })(FormMapper)
      ),
      isValid: false,
      isInvalid: false,
      required: true,
    },
    elementDecorators: {
      label: "[SECTION 1.4] Vision",
      sublabel: "",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
  },

  theme_topics: {
    elementType: "checkboxes-inline",
    elementConfig: {
      choices: ((_) => {
        // This lambda automatically extracts the lower-level themes.
        let out = [];
        for (const entry of Object.entries(_)) {
          if (entry[0].startsWith("theme") && entry[0].includes("-")) {
            out.push({
              value: entry[0],
              label: entry[1],
            });
          }
        }
        return out.sort((a, b) => {
          return a.value.localeCompare(b.value);
        });
      })(FormMapper),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "[SECTION 1.5] Research Topics",
      sublabel: "",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: [],
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
  },

  //open_theme: {
  //  elementType: "input-inline",
  //  elementConfig: {
  //    type: "text",
  //    placeholder: "(input exmple: opentheme1,opentheme2,opentheme3)",
  //    isValid: false,
  //    isInvalid: false,
  //    disabled: true,
  //  },
  //  elementDecorators: {
  //    label: "[SECTION 1.6] Open Theme: AI Fundamental Research",
  //    sublabel: "Specify up to THREE (3) keywords)",
  //    feedback: "valid",
  //    feedbackMsg: "",

  //  },
  //  validation: {
  //    isText: true,
  //    maxLength: 200,
  //    required: false,
  //  },
  //  value: "",
  //  valid: true,
  //  touched: false,
  //},

  //theme_others: {
  //  elementType: "input-inline",
  //  elementConfig: {
  //    type: "text",
  //    placeholder: "(input exmple: othertopic1,othertopic2,othertopic3...)",
  //    isValid: false,
  //    isInvalid: false,
  //    disabled: false,
  //  },
  //  elementDecorators: {
  //    label: "[SECTION 1.7] Other Sub-Theme(s)",
  //    sublabel: "",
  //    feedback: "valid",
  //    feedbackMsg: "",
  //  },
  //  validation: {
  //    isText: true,
  //    maxLength: 200,
  //    required: false,
  //  },
  //  value: "",
  //  valid: true,
  //  touched: false,
  //},
  core_tech_areas: {
    elementType: "checkboxes-inline",
    elementConfig: {
      // FIXME Try to extract this from one place like themes.  May need to
      // rename the values so that it can be lexicographically sorted correctly.
      choices: [
        { value: "core1", label: "Cognitive modelling and systems" },
        { value: "core2", label: "Game theory and economic paradigms" },
        { value: "core3", label: "Heuristic search and optimization" },
        { value: "core4", label: "Knowledge representation and reasoning" },
        { value: "core5", label: "Machine learning" },
        { value: "core6", label: "Multiagent systems" },
        { value: "core7", label: "Natural language processing (NLP)" },
        { value: "core8", label: "Planning and scheduling" },
        { value: "core9", label: "Reasoning under uncertainty" },
        { value: "core10", label: "Robotics" },
        { value: "core11", label: "Search and constraint satisfaction" },
        { value: "core12", label: "Vision" },
      ],
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label:
        "[SECTION 1.6] Classification of AI Core Technical Areas of the Proposal",
      sublabel: "",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: [],
    validation: {
      maxChoices: 3,
      required: true,
    },
    valid: false,
    touched: false,
  },

  team_information: {
    elementType: "input-cascade",
    elementConfig: {
      data: {
        header: {
          role: {
            label: "Role",
            sublabel: "",
            config: {
              type: "select",
              options: [
                { value: "", label: "(select a role from drop-down)" },
                {
                  value: "PI[AI]",
                  label: "Principal Investigator (Technical AI Expert)",
                },
                {
                  value: "PI[SH]",
                  label: "Principal Investigator (SHAPE/HASS Expert)",
                },
                {
                  value: "Co-PI[AI]",
                  label: "Co-Principal Investigator (Technical AI Expert)",
                },
                {
                  value: "Co-PI[SH]",
                  label: "Co-Principal Investigator (SHAPE/HASS Expert)",
                },
                { value: "collab", label: "Collaborator" },
              ],
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          firstname: {
            label: "First Name",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "Given Name",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          lastname: {
            label: "Last Name",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "Family Name",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          gscholar: {
            label: "Google Scholar",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "(max 200 characters)",
              required: false,
            },
            value: "",
            valid: true,
            touched: false,
            validation: {
              maxLength: 200,
              required: false,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          dblp: {
            label: "DBLP",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "(max 200 characters)",
              required: false,
            },
            value: "",
            valid: true,
            touched: false,
            validation: {
              maxLength: 200,
              required: false,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          institution: {
            label: "Institution",
            sublabel:
              "For PI's / Co-PI's institution: write in short-form only, e.g., A*STAR, NTU, NUS, CREATE-CARES, CREATE-SEC, CREATE-IMPERIAL.",
            config: {
              type: "text",
              placeholder: "(max 200 characters)",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          designation: {
            label: "Designation",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "e.g Associate Professor",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          department: {
            label: "Department",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "(max 200 characters)",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          percentage_effort: {
            label: "Percentage Effort %",
            sublabel: "",
            config: {
              type: "number",
              placeholder: "(max 10 characters)",
              required: true,
            },
            value: "0",
            valid: false,
            touched: false,
            validation: {
              isDecimal: true,
              maxLength: 10,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          percentage_time: {
            label: "Percentage Time %",
            sublabel: "",
            config: {
              type: "number",
              placeholder: "(max 10 characters)",
              required: true,
            },
            value: "0",
            valid: false,
            touched: false,
            validation: {
              isDecimal: true,
              maxLength: 10,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
        },
        content: [],
      },
      type: "stack",
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "[SECTION 1.7] Team Members",
      sublabel: (
        <>
          Each team should have either:
          <ol>
            <li>
              Exactly one PI who is a <u>technical AI expert</u>, and at least
              one Co-PI who is a <u>SHAPE/HASS expert</u>; <strong>OR</strong>
            </li>
            <li>
              Exactly one PI who is a <u>SHAPE/HASS expert</u>, and at least one
              Co-PI who is a <u>technical AI expert</u>.
            </li>
          </ol>
        </>
      ),
      feedback: "valid",
      feedbackMsg: "",
    },
    value: [],
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
  },

  proposal: {
    elementType: "upload",
    label: "[SECTION 2] Details of Research Proposal (max 16mb)",
    sublabel: "(Please only upload .pdf format)",
    feedback: "",
    value: "",
    valid: true,
    touched: false,
    loaded: true,
    validation: {
      required: true,
    },
  },

  budget_techaiexperts: {
    elementType: "input-inline",
    elementConfig: {
      type: "number",
      placeholder: "enter amount inclusive of IRC (numerical only)",
      isValid: false,
      isInvalid: false,
      required: true,
      disabled: false,
    },
    elementDecorators: {
      label: "[SECTION 3.1.1] Requested Budget: AI Technical Experts",
      sublabel: "(inclusive of IRC)",
      feedback: "valid",
      feedbackMsg: "",
    },
    validation: {
      isDecimal: true,
      maxLength: 10,
      required: true,
    },
    value: "0.0",
    valid: false,
    touched: false,
  },

  budget_shapehassexperts: {
    elementType: "input-inline",
    elementConfig: {
      type: "number",
      placeholder: "enter amount inclusive of IRC (numerical only)",
      isValid: false,
      isInvalid: false,
      required: true,
      disabled: false,
    },
    elementDecorators: {
      label: "[SECTION 3.1.2] Requested Budget: SHAPE / HASS Experts",
      sublabel: "(inclusive of IRC)",
      feedback: "valid",
      feedbackMsg: "",
    },
    validation: {
      isDecimal: true,
      maxLength: 10,
      required: true,
    },
    value: "0.0",
    valid: false,
    touched: false,
  },

  budget: {
    elementType: "upload",
    label: "[SECTION 3.2 & 3.3] Proposed Budget (max 16mb)",
    sublabel: "(Please only upload .xlsx format)",
    feedback: "",
    value: "",
    valid: true,
    touched: false,
    loaded: true,
    validation: {
      required: true,
    },
  },

  funding_support: {
    elementType: "upload",
    label: "[SECTION 4] Declaration of Other Funding Support (max 16mb)",
    sublabel: "(Please only upload .pdf format)",
    feedback: "",
    value: "",
    valid: true,
    touched: false,
    loaded: true,
    validation: {
      required: true,
    },
  },

  performance_indicators: {
    elementType: "upload",
    label: "[SECTION 5] Performance Indicators (max 16mb)",
    sublabel: "(Please only upload .pdf format)",
    feedback: "",
    value: "",
    valid: true,
    touched: false,
    loaded: true,
    validation: {
      required: true,
    },
  },

  international_reviewers: {
    elementType: "input-cascade",
    elementConfig: {
      data: {
        header: {
          name: {
            label: "Name of Reviewer",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "(max 200 characters)",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              isText: true,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          institution: {
            label: "Institution",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "(max 200 characters)",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          designation: {
            label: "Designation",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "e.g Associate Professor",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          email: {
            label: "E-mail address",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "e.g abc@xyz.com",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          interest: {
            label: "Research interest / Expertise",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
          relationship: {
            label: "Relationship to PI / Co-PI",
            sublabel: "",
            config: {
              type: "text",
              placeholder: "",
              required: true,
            },
            value: "",
            valid: false,
            touched: false,
            validation: {
              maxLength: 200,
              required: true,
            },
            feedback: {
              type: "valid",
              message: "",
            },
          },
        },
        content: [],
      },
      type: "stack",
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "[SECTION 6] Names of Suggested International Reviewers",
      sublabel: "Please nominate at least three(3) reviewers",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: [],
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
  },

  supplement_files: {
    elementType: "upload",
    label: "[SECTION 7] Declaration by Grant Applicants (max 16mb)",
    sublabel: "(Please only upload .pdf format)",
    feedback: "",
    value: "",
    valid: true,
    touched: false,
    loaded: true,
    validation: {
      required: true,
    },
  },

  // supplement_endorsement: {
  //   elementType: "upload",
  //   label: "[Section 8] Endorsement by the Host Institution",
  //   sublabel: "(Please only upload .pdf format)",
  //   feedback: "",
  //   value: "",
  //   valid: true,
  //   touched: false,
  //   loaded: true,
  //   validation: {
  //     required: false,
  //   },
  // },

  project_schedule: {
    elementType: "upload",
    label: "[ANNEX A] Project Implementation Schedule (max 16mb)",
    sublabel: "(Please only upload .pdf format)",
    feedback: "",
    value: "",
    valid: true,
    touched: false,
    loaded: true,
    validation: {
      required: true,
    },
  },

  curriculum_vitae: {
    elementType: "upload",
    label: "[ANNEX B] Curriculum Vitae (max 16mb)",
    sublabel: "(Please only upload .pdf format)",
    feedback: "",
    value: "",
    valid: true,
    touched: false,
    loaded: true,
    validation: {
      required: true,
    },
  },

  letter_of_support: {
    elementType: "upload",
    label:
      "[ANNEX C] Declaration of Other Funding Support Supplementary (max 16mb)",
    sublabel: "(Please only upload .pdf format)",
    feedback: "",
    value: "",
    valid: true,
    touched: false,
    loaded: true,
    validation: {
      required: true,
    },
  },
};

//// Refactored out value mapping.
//const _EVAL_SCALE = [
//  { value: "", label: "(select from drop-down)" },
//  { value: "4", label: "4 - Excellent" },
//  { value: "3", label: "3 - Good" },
//  { value: "2", label: "2 - Average" },
//  { value: "1", label: "1 - Poor" },
//  { value: "0", label: "0 - Unable to Judge / Not Applicable" },
//];
//const _RESOURCE_SCALE = [
//  { value: "", label: "(select from drop-down)" },
//  { value: "4", label: "4 - Adequate" },
//  { value: "3", label: "3 - Excessive" },
//  { value: "2", label: "2 - Inadequate" },
//  { value: "1", label: "1 - Unable to Judge" },
//];
export const reviews = {
  // SECTION A
  scientific: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.scientific)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Scientific Quality",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 0,
  },

  novelty: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.novelty)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Novelty",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 0,
  },

  feasibility: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.feasibility)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Feasibility / Viability / Appropriateness",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 0,
  },

  quality_integration: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.quality_integration)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Quality of Integration",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 0,
  },

  suitability: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.suitability)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Suitability and track record of PI and research team",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 0,
  },

  impact: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.impact)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Impact to the field of AI",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 0,
  },

  manpower: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.manpower)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Manpower",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 1,
  },

  equipment: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.equipment)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Equipment / Facilities",
      sublabel: "(includes compute resources)",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 1,
  },

  other_operating: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.other_operating)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Other Operating Expenditure",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "a",
    part: 1,
  },

  // SECTION B
  support_justification: {
    elementType: "textarea-inline",
    elementConfig: {
      type: "text",
      placeholder:
        "(Please provide comments on, (i) Scientific quality (ii) Novelty (iii) Feasibility / Viability / Appropriateness (iv) Suitability and track record of PI and research team (v) Impact to the field of AI (vi) Resources Requested.)",
      isValid: false,
      isInvalid: false,
      required: true,
      as: "textarea",
      rows: "7",
    },
    elementDecorators: {
      label: "Basis / Justifications for Overall Scoring / Recommendation",
      feedback: "valid",
      feedbackMsg: "",
    },
    validation: {
      isText: true,
      maxLength: 5000,
      required: true,
    },
    value: "",
    valid: false,
    touched: false,
    section: "b",
    part: 1,
  },

  // SECTION C
  overall_score: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.overall_score)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Overall Scoring / Recommendation",
      sublabel:
        "Scoring of 3 & 4 would indicate recommended for funding, 2 would indicate borderline but leaning slightly towards not recommended for funding, and 1 would indicate not recommended for funding.",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "c",
    part: 1,
  },

  level_of_confidence: {
    elementType: "select-inline",
    elementConfig: {
      options: [{ value: "", label: "(select from drop-down)" }].concat(
        _pull_as_options(RevMapper.level_of_confidence)
      ),
      isValid: false,
      isInvalid: false,
    },
    elementDecorators: {
      label: "Level of Confidence in Reviewing this Proposal",
      feedback: "valid",
      feedbackMsg: "",
    },
    value: "",
    validation: {
      required: true,
    },
    valid: true,
    touched: false,
    section: "c",
    part: 2,
  },

  // SECTION D
  strength_of_proposal: {
    elementType: "textarea-inline",
    elementConfig: {
      type: "text",
      placeholder:
        "(Please provide constructive comments on the ​strengths​ of the proposal.)",
      isValid: false,
      isInvalid: false,
      required: true,
      as: "textarea",
      rows: "7",
    },
    elementDecorators: {
      label: "Strengths of the Proposal",
      feedback: "valid",
      feedbackMsg: "",
    },
    validation: {
      isText: true,
      maxLength: 5000,
      required: true,
    },
    value: "",
    valid: false,
    touched: false,
    section: "d",
    part: 1,
  },

  suggestion_for_improvement: {
    elementType: "textarea-inline",
    elementConfig: {
      type: "text",
      placeholder:
        "(Please provide constructive comments on the ​weaknesses​ of the proposal. How can the proposal be modified, augmented, reduced or structured for greater impact?)",
      isValid: false,
      isInvalid: false,
      required: true,
      as: "textarea",
      rows: "7",
    },
    elementDecorators: {
      label: "Suggestions for Improvement",
      feedback: "valid",
      feedbackMsg: "",
    },
    validation: {
      isText: true,
      maxLength: 5000,
      required: true,
    },
    value: "",
    valid: false,
    touched: false,
    section: "d",
    part: 2,
  },
};

export const endorse = {
  endorse_file: {
    elementType: "file",
    elementConfig: {
      type: "file",
      isInvalid: false,
      required: true,
    },
    elementDecorators: {
      label: "Endorsement Page (Upload)",
      feedback: "valid",
      feedbackMsg: "",
    },
    validation: {
      isFile: true,
    },
    value: "",
    selectedFile: null,
    valid: false,
    touched: false,
  },
};
